"use client";

import { useEffect, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Dialog, DialogClose, DialogContent } from "@/shadcn/ui/dialog";
import { cn } from "@/lib/utils";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import { useCtaHref } from "./ctaHref";

/** Every link to this anchor on the page opens the dialog instead of scrolling. */
export const HOW_IT_WORKS_HASH = "#how-it-works";

/** "works" selection: Figma 148×49 at 48px, measured against the glyphs; em-based, so it scales with the mobile title. */
const TITLE_SELECTION_GEOMETRY: SelectionGeometry = {
  font: 48,
  box: [-14.19, -2.98, 148, 49],
  word: 117.67,
  handles: [[0, 0, 2, 43], [-2, 43, 6, 6], [144, 0, 6, 6], [146, 0, 2, 49]],
};

const LABEL = "mb-0 font-geist text-[1.75rem] leading-[normal] font-semibold tracking-[0.105rem] whitespace-nowrap text-[#0b5b45]";
const BODY = "mb-0 font-geist text-[2rem] leading-[1.5] font-normal text-[rgba(0,27,20,0.64)]";

export default function TradingCompetitionHowItWorks({ data }: { data: PageTradingCompetitionSchema }) {
  const [open, setOpen] = useState(false);

  // Open from any "#how-it-works" link (hero, leaderboard, mobile menu) and from a shared URL.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a");
      if (!a || a.getAttribute("href") !== HOW_IT_WORKS_HASH) return;
      e.preventDefault();
      setOpen(true);
    };
    document.addEventListener("click", onClick, true);
    if (window.location.hash === HOW_IT_WORKS_HASH) setOpen(true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next && window.location.hash === HOW_IT_WORKS_HASH) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  const ctaHref = useCtaHref(data.hiwCta, data.settings?.language);
  const onCta = () => {
    setOpen(false);
    const href = stegaClean(ctaHref);
    if (!href) return;
    // Let the dialog unmount (and the scroll lock lift) before following the link.
    window.setTimeout(() => {
      if (href.startsWith("#")) document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      else window.location.href = href;
    }, 220);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        aria-describedby={undefined}
        className="!w-[min(122.75rem,calc(100%-2rem))] border border-solid border-[rgba(0,27,20,0.1)] bg-white">
        <DialogClose
          aria-label="Close"
          className="absolute top-[1.5rem] right-[1.75rem] z-10 flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-[0.25rem] border border-solid border-[rgba(0,27,20,0.12)] bg-white">
          <svg aria-hidden="true" viewBox="0 0 18 18" fill="none" className="h-[2.25rem] w-[2.25rem]">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="#001B14" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </DialogClose>

        <div
          data-lenis-prevent
          className="flex max-h-[calc(100dvh-2rem)] flex-col items-start gap-[3rem] overflow-y-auto [&>*]:shrink-0 overscroll-contain px-[2.5rem] pt-[4rem] pb-[4rem] lg:max-h-[calc(100dvh-6rem)] lg:px-[6rem] lg:pt-[5rem] lg:pb-[6rem]">
          {data.hiwEyebrow && (
            <p className="mb-0 pr-[6rem] font-blacker text-[2.25rem] leading-[0.9444] font-normal text-[rgba(0,27,20,0.66)] italic">
              {data.hiwEyebrow}
            </p>
          )}

          <DialogPrimitive.Title asChild>
            <h2 className="mb-0 font-geist text-[5rem] leading-[1.05] font-medium tracking-[-0.2rem] whitespace-pre text-[#001b14] lg:text-[6rem] lg:tracking-[-0.24rem]">
              {data.hiwTitleBefore}
              {"   "}
              <span className="relative font-blacker leading-[0] font-normal text-[#003828] italic">
                <SelectionHighlight
                  mobile={TITLE_SELECTION_GEOMETRY}
                  desktop={TITLE_SELECTION_GEOMETRY}
                  fill="#e2f3ef"
                  handle="#228e71"
                />
                <span className="relative font-blacker">{data.hiwTitleHighlight}</span>
              </span>
            </h2>
          </DialogPrimitive.Title>

          {data.hiwVideo?.src && (
            <video
              src={data.hiwVideo.src}
              poster={data.hiwVideoPoster?.src}
              controls
              playsInline
              preload="metadata"
              className="block h-auto w-full rounded-[0.5rem] bg-[#001b14]"
            />
          )}

          {data.hiwSummary && (
            <div className="flex w-full flex-col items-start gap-[1rem] overflow-hidden rounded-[0.5rem] border border-solid border-[rgba(0,27,20,0.08)] bg-[rgba(0,27,20,0.04)] p-[2.5rem] lg:p-[3rem]">
              <p className={LABEL}>{data.hiwSummaryLabel}</p>
              <p className="mb-0 max-w-[78.25rem] font-geist text-[2.25rem] leading-[1.4] font-normal text-[#001b14]">
                {data.hiwSummary}
              </p>
            </div>
          )}

          {!!data.hiwSteps?.length && (
            <ol className="m-0 flex w-full list-none flex-col gap-[3rem] p-0">
              {data.hiwSteps.map((step, i) => (
                <li key={step._key ?? i} className="flex flex-col gap-[3rem]">
                  {/* Figma's divider takes no height: a 1px line drawn just above its slot */}
                  {i > 0 && (
                    <i aria-hidden="true" className="relative h-0 w-full">
                      <i className="absolute inset-x-0 top-[-1px] h-px bg-[rgba(0,27,20,0.08)]" />
                    </i>
                  )}
                  <div className="flex w-full items-start gap-[2rem]">
                    <p className="mb-0 font-blacker text-[5rem] leading-[0.9444] font-normal whitespace-nowrap text-[#001b14] italic">
                      {i + 1}
                    </p>
                    <div className="flex min-w-0 flex-1 flex-col items-start gap-[0.5rem]">
                      <p className="mb-0 font-geist text-[2.5rem] leading-[normal] font-medium tracking-[-0.025rem] text-[#001b14]">
                        {step.title}
                      </p>
                      {step.body && <p className={cn(BODY, "max-w-[79.625rem] whitespace-pre-line")}>{step.body}</p>}
                      {!!step.chips?.length && (
                        <div className="flex flex-wrap items-start gap-[1rem]">
                          {step.chips.map((chip, j) => (
                            <div
                              key={chip._key ?? j}
                              className="flex h-[4.5rem] items-center gap-[1rem] rounded-[0.25rem] border border-solid border-[rgba(0,27,20,0.1)] px-[1.5rem] font-geist text-[1.75rem] leading-[normal] whitespace-nowrap">
                              <p className="mb-0 font-semibold text-[#0b5b45]">{chip.value}</p>
                              <p className="mb-0 font-normal text-[rgba(0,27,20,0.64)]">{chip.label}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {step.note && (
                        <p className="mb-0 font-geist text-[1.75rem] leading-[normal] font-normal text-[rgba(0,27,20,0.42)]">
                          {step.note}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}

          {!!data.hiwExampleRows?.length && (
            <div className="flex w-full flex-col overflow-hidden rounded-[0.5rem] border border-solid border-[rgba(0,27,20,0.08)]">
              <div className="py-[1.75rem] pl-[2.5rem]">
                <p className={LABEL}>{data.hiwExampleLabel}</p>
              </div>
              {data.hiwExampleRows.map((row, i) => (
                <div
                  key={row._key ?? i}
                  className="flex min-h-[8rem] w-full items-center gap-[1.5rem] border-t border-solid border-[rgba(0,27,20,0.08)] px-[2.5rem] py-[1.75rem] font-geist text-[2rem] leading-[normal] lg:h-[12.5rem]">
                  <p className="mb-0 font-normal text-[rgba(0,27,20,0.64)]">{row.account}</p>
                  <p className="mb-0 font-medium whitespace-nowrap text-[#001b14]">{row.calculation}</p>
                  <i aria-hidden="true" className="min-w-0 flex-1" />
                  <p className="mb-0 font-semibold whitespace-nowrap text-[#001b14]">{row.result}</p>
                </div>
              ))}
              {data.hiwExampleTotal && (
                <div className="flex min-h-[8rem] w-full items-center gap-[1.5rem] border-t border-solid border-[rgba(0,27,20,0.08)] bg-[#e2f3ef] px-[2.5rem] py-[1.75rem] font-geist leading-[normal] lg:h-[12.5rem]">
                  <p className="mb-0 text-[2rem] font-normal text-[#001b14]">{data.hiwExampleTotalLabel}</p>
                  <i aria-hidden="true" className="min-w-0 flex-1" />
                  <p className="mb-0 text-[2.5rem] font-semibold whitespace-nowrap text-[#0b5b45]">{data.hiwExampleTotal}</p>
                </div>
              )}
            </div>
          )}

          {!!data.hiwFacts?.length && (
            <div className="flex w-full flex-col items-start">
              <p className={LABEL}>{data.hiwFactsLabel}</p>
              {data.hiwFacts.map((fact, i) => (
                <div
                  key={fact._key ?? i}
                  className="w-full border-solid border-[rgba(0,27,20,0.08)] py-[1.75rem] [&:not(:last-child)]:border-b">
                  <p className={BODY}>
                    <span className="font-geist font-medium text-[#001b14]">{fact.lead}</span> {fact.text}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-[3rem] border-t border-solid border-[rgba(0,27,20,0.08)] pt-[3rem]">
            {data.hiwTerms && (
              <p className="mb-0 font-geist text-[1.75rem] leading-[normal] font-normal text-[rgba(0,27,20,0.42)]">{data.hiwTerms}</p>
            )}
            {data.hiwCta?.text && (
              <button
                type="button"
                onClick={onCta}
                className="flex h-[5.875rem] items-center gap-[1.25rem] rounded-[0.25rem] bg-[#001b14] pr-[2.5rem] pl-[3rem]">
                <span className="font-geist text-[2rem] leading-[normal] font-semibold tracking-[-0.04rem] whitespace-nowrap text-white">
                  {data.hiwCta.text}
                </span>
                <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-[2.5rem] w-[2.5rem] shrink-0">
                  <path
                    d="M4.16667 10H15.8333M10 4.16667L15.8333 10L10 15.8333"
                    stroke="white"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
